// Wi-Fi settings for Epson printers, written over the Bluetooth link with
// getPrinterSettingEx/setPrinterSettingEx. Key names come from Epson's
// JSON_Spec_sheet (Setting/WifiCfg); only TM-m30III/m50 class printers have it.

import type { WifiNetwork } from "@app/vue/providers/bridge-rpc-types";

export const WIFI_SECURITY = ["WPA/WPA2-PSK", "WPA3-SAE(AES)"] as const;

export type WifiSecurity = (typeof WIFI_SECURITY)[number];

export const IP_MODES = ["Auto", "Manual"] as const;

export type IpMode = (typeof IP_MODES)[number];

// Setting/TcpIpCfg/IpV4. Address fields only matter for "Manual".
export type Ipv4Config = {
  mode: IpMode;
  ip: string;
  subnetMask: string;
  gateway: string;
};

export type WifiConfig = {
  ssid: string;
  security: WifiSecurity;
  passphrase: string;
  ipv4: Ipv4Config;
};

export type WifiFormError =
  | "ssid_required"
  | "ssid_invalid"
  | "passphrase_invalid"
  | "ip_invalid"
  | "subnet_invalid"
  | "gateway_invalid";

const PRINTABLE_ASCII = /^[\x20-\x7e]*$/;

const IPV4 =
  /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

export const isIpv4 = (value: string) => IPV4.test(value.trim());

export const validateWifiConfig = (
  config: WifiConfig,
): WifiFormError | null => {
  if (!config.ssid) return "ssid_required";
  if (config.ssid.length > 32 || !PRINTABLE_ASCII.test(config.ssid)) {
    return "ssid_invalid";
  }
  const { passphrase } = config;
  if (
    passphrase.length < 8 ||
    passphrase.length > 63 ||
    !PRINTABLE_ASCII.test(passphrase)
  ) {
    return "passphrase_invalid";
  }
  const { ipv4 } = config;
  if (ipv4.mode === "Manual") {
    if (!isIpv4(ipv4.ip)) return "ip_invalid";
    if (!isIpv4(ipv4.subnetMask)) return "subnet_invalid";
    if (!isIpv4(ipv4.gateway)) return "gateway_invalid";
  }
  return null;
};

export class WifiUnsupportedError extends Error {
  constructor() {
    super("Printer settings have no Setting.WifiCfg");
    this.name = "WifiUnsupportedError";
  }
}

type SettingsDocument = {
  Setting?: {
    WifiCfg?: Record<string, unknown>;
    TcpIpCfg?: Record<string, unknown>;
  } & Record<string, unknown>;
} & Record<string, unknown>;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

// The printer prints its address on every start, so staff can read it off
// paper whichever way it was assigned.
const ipv4Settings = (current: unknown, ipv4: Ipv4Config) => {
  const settings: Record<string, unknown> = {
    ...asRecord(current),
    Acquiring_Select: ipv4.mode,
    IpAddlessPrint_Select: "Enable",
  };
  if (ipv4.mode === "Manual") {
    settings.Ip = ipv4.ip.trim();
    settings.Sm = ipv4.subnetMask.trim();
    settings.Gw = ipv4.gateway.trim();
  }
  return settings;
};

/**
 * Returns the printer's own settings JSON with the Wi-Fi network and IPv4
 * settings replaced.
 * Epson asks for the JSON read from the same printer, so everything else is
 * sent back untouched.
 */
export const applyWifiConfig = (
  settingsJson: string,
  config: WifiConfig,
): string => {
  let settings: SettingsDocument;
  try {
    settings = JSON.parse(settingsJson) as SettingsDocument;
  } catch {
    throw new WifiUnsupportedError();
  }
  const wifiCfg = settings?.Setting?.WifiCfg;
  if (!wifiCfg || typeof wifiCfg !== "object") {
    throw new WifiUnsupportedError();
  }
  const tcpIpCfg = asRecord(settings.Setting?.TcpIpCfg);

  return JSON.stringify({
    ...settings,
    Setting: {
      ...settings.Setting,
      WifiCfg: {
        ...wifiCfg,
        Ssid: config.ssid,
        EncType_Select: config.security,
        WpaPsk: { ...asRecord(wifiCfg.WpaPsk), Key: config.passphrase },
      },
      TcpIpCfg: {
        ...tcpIpCfg,
        IpV4: ipv4Settings(tcpIpCfg.IpV4, config.ipv4),
      },
    },
  });
};

export type SavedWifi = { ssid: string; security: string };

// The printer never returns WpaPsk.Key, so only the network and security
// type can be confirmed.
export type WifiCheck =
  | { status: "match"; saved: SavedWifi }
  | { status: "mismatch"; saved: SavedWifi }
  | { status: "unreachable"; detail: string };

export const readSavedWifi = (settingsJson: string): SavedWifi => {
  let wifiCfg: Record<string, unknown> | undefined;
  try {
    wifiCfg = (JSON.parse(settingsJson) as SettingsDocument)?.Setting?.WifiCfg;
  } catch {
    wifiCfg = undefined;
  }
  const read = (value: unknown) => (typeof value === "string" ? value : "");
  return { ssid: read(wifiCfg?.Ssid), security: read(wifiCfg?.EncType_Select) };
};

export const checkSavedWifi = (
  settingsJson: string,
  config: WifiConfig,
): WifiCheck => {
  const saved = readSavedWifi(settingsJson);
  const matches =
    saved.ssid === config.ssid && saved.security === config.security;
  return { status: matches ? "match" : "mismatch", saved };
};

// Setting JSON from an unsupported printer is "", which has no serial.
export const readSerialNumber = (settingsJson: string): string => {
  try {
    const product = asRecord(
      asRecord((JSON.parse(settingsJson) as SettingsDocument).PrinterSpec)
        .Product,
    );
    return typeof product.SerialNo === "string" ? product.SerialNo.trim() : "";
  } catch {
    return "";
  }
};

export type WifiBand = "2.4 GHz" | "5 GHz" | "6 GHz" | "Other";

export const wifiBand = (frequency: number): WifiBand =>
  frequency >= 2400 && frequency < 2500
    ? "2.4 GHz"
    : frequency >= 4900 && frequency < 5925
      ? "5 GHz"
      : frequency >= 5925 && frequency < 7125
        ? "6 GHz"
        : "Other";

export type PrinterWifiNetwork = {
  ssid: string;
  /** Strongest signal of the network's access points, in dBm */
  signal: number;
  security: WifiSecurity;
};

// The app's name for networks that don't broadcast one.
const HIDDEN_SSID = "(hidden SSID)";

// Android capabilities look like "[WPA2-PSK-CCMP][RSN-SAE-CCMP][ESS]";
// WPA3-only networks offer SAE without PSK.
const wifiSecurity = (capabilities: string): WifiSecurity =>
  capabilities.includes("SAE") && !capabilities.includes("PSK")
    ? "WPA3-SAE(AES)"
    : "WPA/WPA2-PSK";

/**
 * Networks the printer can join, strongest first. Epson printers only have
 * 2.4 GHz radios, and a network with several access points is listed once.
 */
export const printerWifiNetworks = (
  networks: WifiNetwork[],
): PrinterWifiNetwork[] => {
  const bySsid = new Map<string, PrinterWifiNetwork>();
  for (const network of networks) {
    if (wifiBand(network.frequency) !== "2.4 GHz") continue;
    if (!network.ssid || network.ssid === HIDDEN_SSID) continue;
    const known = bySsid.get(network.ssid);
    if (known && known.signal >= network.level) continue;
    bySsid.set(network.ssid, {
      ssid: network.ssid,
      signal: network.level,
      security: wifiSecurity(network.capabilities),
    });
  }
  return [...bySsid.values()].sort((a, b) => b.signal - a.signal);
};
